// Step 5d Phase 5 — rolling-cache window math (pure; unit-testable without a browser).
//
// The rolling cache covers the home base's local −2/+7 days. To stay in the BOARD's
// keyspace (so a coincident displayed window is the SAME scope, not a duplicate — recon
// item 2), the UTC bounds are computed with the exact `zonedDayRangeUtc`/`addDays`
// discipline `loadBookings` uses, unioned across all bases and widened ±1 day. Widening the
// span to [today−(back+1) … today+(fwd+1)] guarantees the wide scope is a SUPERSET of every
// day-view window for a date in [today−back … today+fwd] (each day view itself widens ±1),
// so `readCoveringBookings` can reconstruct any of those days offline.

import { zonedDayRangeUtc, addDays, todayInTz } from '../../components/ops-board/theme';

export const ROLLING_BACK_DAYS = 2;
export const ROLLING_FWD_DAYS = 7;

/** Home base local "today" (the anchor date for the rolling span). */
export function homeToday(homeTz: string): string {
  return todayInTz(homeTz);
}

/**
 * UTC [from,to] covering the rolling span, unioned across all base timezones and widened ±1d
 * (matching loadBookings). `today` is the home-base local date; pass it explicitly so this is
 * pure/testable. Returns null when there are no bases.
 */
export function rollingWindow(
  bases: ReadonlyArray<{ timezone: string }>,
  today: string,
  back: number = ROLLING_BACK_DAYS,
  fwd: number = ROLLING_FWD_DAYS,
): { from: string; to: string } | null {
  if (bases.length === 0) return null;
  const ranges = bases.map((b) => ({
    from: zonedDayRangeUtc(addDays(today, -(back + 1)), b.timezone).from,
    to: zonedDayRangeUtc(addDays(today, fwd + 1), b.timezone).to,
  }));
  return {
    from: ranges.map((r) => r.from).sort()[0],
    to: ranges.map((r) => r.to).sort().slice(-1)[0],
  };
}

/**
 * UTC [from,to] for a manually picked DATE RANGE [fromDate … toDate] (inclusive), unioned
 * across base timezones and widened ±1d — same keyspace + coverage discipline as the rolling
 * window, so a pinned range is readable at board granularity offline. Returns null if no bases
 * or the range is inverted.
 */
export function rangeWindow(
  bases: ReadonlyArray<{ timezone: string }>,
  fromDate: string,
  toDate: string,
): { from: string; to: string } | null {
  if (bases.length === 0 || fromDate > toDate) return null;
  const ranges = bases.map((b) => ({
    from: zonedDayRangeUtc(addDays(fromDate, -1), b.timezone).from,
    to: zonedDayRangeUtc(addDays(toDate, 1), b.timezone).to,
  }));
  return {
    from: ranges.map((r) => r.from).sort()[0],
    to: ranges.map((r) => r.to).sort().slice(-1)[0],
  };
}

/** Does [outerFrom,outerTo] fully cover [innerFrom,innerTo]? (ISO UTC strings sort chronologically.) */
export function windowCovers(outerFrom: string, outerTo: string, innerFrom: string, innerTo: string): boolean {
  return outerFrom <= innerFrom && outerTo >= innerTo;
}
